import { supabase, DbQuiz, DbQuestion, DbQuizAttempt } from '../lib/supabase';
import { Question, Quiz } from '../contexts/QuizContext';
import { generateAudioServerSide } from './audioJobService';
import { logger } from '../utils/logger';
import { detectDeviceType } from '../utils/device';

/** What check_answer returns once an answer has been submitted. */
export interface AnswerCheck {
  correct: boolean;
  /** The stored answer: the first one submitted, which is final. */
  selected: number[];
  correctOptions: string[];
  explanation: string;
  explanationAudioUrl: string;
}

// Add backup functionality
const backupService = {
  // 'pre_delete' snapshots are taken by removeQuiz immediately before a module
  // is deleted — they are what the Backups tab's "Deleted Modules" section
  // restores from.
  async createBackup(quizId: string, type: 'auto' | 'manual' | 'pre_delete' = 'auto') {
    // Get current quiz state
    const { data: quiz, error: quizError } = await supabase
      .from('quizzes')
      .select('*, questions(*)')
      .eq('id', quizId)
      .single();

    if (quizError) throw quizError;

    const sources = {
      auto: 'pre_update_backup',
      manual: 'manual_backup',
      pre_delete: 'pre_delete_backup',
    } as const;

    // Create backup entry
    const { data: backup, error: backupError } = await supabase
      .from('quiz_backups')
      .insert({
        quiz_id: quizId,
        quiz_data: quiz,
        created_at: new Date().toISOString(),
        backup_type: type,
        metadata: {
          question_count: quiz.questions?.length || 0,
          version: '1.0',
          source: sources[type],
          quiz_title: quiz.title
        }
      })
      .select()
      .single();

    if (backupError) {
      logger.error('Failed to create backup:', backupError);
      throw new Error(`Failed to create backup: ${backupError.message}`);
    }
    return backup;
  },

  async restoreFromBackup(backupId: string) {
    // Get backup data
    const { data: backup, error: backupError } = await supabase
      .from('quiz_backups')
      .select('*')
      .eq('id', backupId)
      .single();

    if (backupError) throw backupError;

    const quizData = backup.quiz_data;

    // quiz_data is the joined row — the nested questions array is NOT a
    // quizzes column and PostgREST rejects unknown keys, so it must be split
    // out before the upsert. Upserting by the ORIGINAL id is what makes this
    // work for deleted modules too: no conflict row means a clean re-insert
    // with the same uuid, which keeps question audio URLs and the module's
    // old backup history pointing at the right place.
    const { questions: backupQuestions, ...quizRow } = quizData;

    const { error: quizError } = await supabase
      .from('quizzes')
      .upsert(quizRow);

    if (quizError) throw quizError;

    // Restore questions with their original ids
    if (backupQuestions) {
      const { error: questionsError } = await supabase
        .from('questions')
        .upsert(backupQuestions.map((q: any) => ({
          ...q,
          quiz_id: quizData.id
        })));

      if (questionsError) throw questionsError;
    }

    // Stamp the backup as used — the Backups tab shows this.
    await supabase
      .from('quiz_backups')
      .update({ restored_at: new Date().toISOString() })
      .eq('id', backupId);

    return quizData;
  },

  async listBackups(quizId: string) {
    const { data, error } = await supabase
      .from('quiz_backups')
      .select('*')
      .eq('quiz_id', quizId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  /**
   * Deleted modules that can be recovered: the newest pre_delete snapshot of
   * each quiz that no longer exists. Client-side filter — a handful of rows.
   */
  async listDeletedModuleBackups() {
    const [{ data: backups, error: backupsError }, { data: liveQuizzes, error: quizzesError }] =
      await Promise.all([
        supabase
          .from('quiz_backups')
          .select('*')
          .eq('backup_type', 'pre_delete')
          .order('created_at', { ascending: false }),
        supabase.from('quizzes').select('id'),
      ]);

    if (backupsError) throw backupsError;
    if (quizzesError) throw quizzesError;

    const liveIds = new Set((liveQuizzes || []).map(q => q.id));
    const seen = new Set<string>();
    return (backups || []).filter(b => {
      if (liveIds.has(b.quiz_id) || seen.has(b.quiz_id)) return false;
      seen.add(b.quiz_id);
      return true;
    });
  }
};

export const quizService = {
  async createQuiz(quiz: Omit<DbQuiz, 'id' | 'created_at' | 'updated_at'>) {
    logger.log('Creating quiz with data:', quiz);
    const { data, error } = await supabase
      .from('quizzes')
      .insert({
        title: quiz.title,
        description: quiz.description,
        passing_score: quiz.passing_score,
        media_url: quiz.media_url,
        is_enabled: quiz.is_enabled,
        order: quiz.order,
        created_by: quiz.created_by,
        has_introduction: quiz.has_introduction,
        introduction_title: quiz.introduction_title,
        introduction_content: quiz.introduction_content,
        introduction_media_url: quiz.introduction_media_url,
        introduction_min_read_time: quiz.introduction_min_read_time ?? 0,
        time_limit_minutes: quiz.time_limit_minutes ?? null,
        renewal_months: quiz.renewal_months ?? null,
        agency_staff_required: quiz.agency_staff_required ?? true,
      })
      .select()
      .single();

    if (error) {
      logger.error('Error creating quiz:', error);
      throw error;
    }
    logger.log('Quiz created successfully:', data);
    return data;
  },

  async createQuestions(questions: Omit<DbQuestion, 'id' | 'created_at'>[]) {
    logger.log('Creating questions with data:', questions);
    // Validate question data before sending to Supabase
    questions.forEach((q, index) => {
      if (!q.quiz_id) throw new Error(`Question ${index} missing quiz_id`);
      if (!q.type) throw new Error(`Question ${index} missing type`);
      if (!q.text && q.type !== 'click-for-answer') throw new Error(`Question ${index} missing text`);
      
      // Ensure arrays are properly formatted for Supabase
      if (q.options) q.options = Array.from(q.options);
      if (q.correct_options) q.correct_options = Array.from(q.correct_options);
      if (q.hotspots) q.hotspots = Array.from(q.hotspots);
      if (q.difference_hotspots) q.difference_hotspots = Array.from(q.difference_hotspots);
      
      // Set default minimum read time for information content
      if (q.type === 'information' && typeof q.minimum_read_time === 'undefined') {
        q.minimum_read_time = 15; // Default to 15 seconds
      }
    });

    const { data, error } = await supabase
      .from('questions')
      .insert(questions)
      .select();

    if (error) {
      logger.error('Error in createQuestions:', error);
      throw error;
    }
    logger.log('Questions created successfully:', data);
    return data;
  },

  async getQuizzes() {
    // First get all questions directly to check for orphaned ones
    const { data: allQuestions, error: questionsError } = await supabase
      .from('questions')
      .select('*')
      .order('created_at', { ascending: false });

    if (questionsError) {
      logger.error('Error checking for questions:', questionsError);
    } else {
      logger.log('Total questions in database:', allQuestions?.length || 0);
      if (allQuestions?.length > 0) {
        logger.log('Question IDs:', allQuestions.map(q => q.id));
        logger.log('Question texts:', allQuestions.map(q => q.text));
      }
    }

    // Now get quizzes with their questions
    const { data, error } = await supabase
      .from('quizzes')
      .select(`
        *,
        questions (*)
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  async getQuizById(id: string) {
    const { data, error } = await supabase
      .from('quizzes')
      .select(`
        *,
        questions (*)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  /** Resuming on a different device than last time adds it to the attempt's
   *  device list (Attempt History shows e.g. "Computer → Phone"). Best effort:
   *  a failure here must never stop someone carrying on with their module. */
  async recordAttemptDevice(attemptId: string): Promise<void> {
    const { error } = await supabase.rpc('record_attempt_device', {
      p_attempt_id: attemptId,
      p_device: detectDeviceType(),
    });
    if (error) logger.warn('record_attempt_device failed', error);
  },

  /** Start Module. The database creates the attempt for the signed-in person,
   *  and only for a module they may take; started_at is stamped there too
   *  (trigger stamp_attempt_times), not by the PC clock. */
  async startQuizAttempt(quizId: string): Promise<DbQuizAttempt> {
    const { data, error } = await supabase.rpc('start_attempt', {
      p_quiz_id: quizId,
      p_device: detectDeviceType(),
    });

    if (error) throw error;
    return data;
  },

  /** One module's questions as a learner gets them: marked questions come
   *  without their answer and explanation (learner_questions). */
  async getLearnerQuestions(quizId: string): Promise<DbQuestion[]> {
    const { data, error } = await supabase.rpc('learner_questions', { p_quiz_id: quizId });
    if (error) throw error;
    return data ?? [];
  },

  /**
   * Submit on a marked question. The database stores the first answer — it
   * is final, so a later call (say after a resume) gets the stored one back —
   * and only then says whether it was right and reveals the right options and
   * the explanation. A call after the time limit fails with hint 'time_up'.
   */
  async checkAnswer(attemptId: string, questionId: string, selected: number[]): Promise<AnswerCheck> {
    const { data, error } = await supabase.rpc('check_answer', {
      p_attempt_id: attemptId,
      p_question_id: questionId,
      p_selected: selected,
    });
    if (error) throw error;
    return {
      correct: !!data.correct,
      selected: (data.selected ?? []).map(Number),
      correctOptions: (data.correct_options ?? []).map(String),
      explanation: data.explanation ?? '',
      explanationAudioUrl: data.explanation_audio_url ?? '',
    };
  },

  /**
   * Seconds left on the attempt's time limit BY THE SERVER CLOCK (null when
   * the module has no limit). The player counts down from this rather than
   * from started_at + limit against Date.now(): a PC clock 17 minutes slow
   * made one person's countdown disagree with Attempt History by 17 minutes
   * on rollout day (25 Sep 2026).
   */
  async getAttemptSecondsLeft(attemptId: string): Promise<number | null> {
    const { data, error } = await supabase.rpc('attempt_seconds_left', { p_attempt_id: attemptId });
    if (error) throw error;
    return data ?? null;
  },

  /** Which question to resume at. Answers are stored by checkAnswer only. */
  async saveQuizProgress(attemptId: string, currentQuestionIndex: number): Promise<void> {
    const { error } = await supabase.rpc('save_attempt_progress', {
      p_attempt_id: attemptId,
      p_question_index: currentQuestionIndex,
    });

    if (error) {
      logger.error('Error saving quiz progress:', error);
    }
  },

  /**
   * The module's time limit ran out. Kept distinct from a completed attempt:
   * completed stays false so no certificate is issued and the assignment
   * stays open; score is 0 and the answers already stored are kept for the
   * admin breakdown.
   */
  async timeOutAttempt(attemptId: string): Promise<void> {
    const { error } = await supabase.rpc('time_out_attempt', { p_attempt_id: attemptId });
    if (error) {
      logger.error('Error timing out attempt:', error);
      throw error;
    }
  },

  /**
   * The page lost focus while a question was showing (tab switch, Alt+Tab,
   * minimise). Only the count is stored, and it can only go up; the player
   * decides what to show.
   */
  async recordFocusLoss(attemptId: string, focusLosses: number): Promise<void> {
    const { error } = await supabase.rpc('record_attempt_focus_loss', {
      p_attempt_id: attemptId,
      p_focus_losses: focusLosses,
    });
    if (error) logger.error('Error recording focus loss:', error);
  },

  async getInProgressAttempt(userId: string, quizId: string): Promise<DbQuizAttempt | null> {
    const { data, error } = await supabase
      .from('quiz_attempts')
      .select('*')
      .eq('user_id', userId)
      .eq('quiz_id', quizId)
      .eq('status', 'in_progress')
      .eq('completed', false)
      .order('started_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      logger.error('Error fetching in-progress attempt:', error);
      return null;
    }
    return data;
  },

  async cancelAttempt(attemptId: string): Promise<void> {
    // Deliberately does NOT set completed = true. A cancelled attempt was not
    // completed, and both the user and admin analytics select on
    // completed = true — so marking cancelled attempts as completed counted every
    // abandoned run as a 0% result and dragged averages down.
    // getInProgressAttempt already excludes these via status = 'in_progress'.
    // Only an in-progress attempt can be cancelled.
    const { error } = await supabase.rpc('cancel_attempt', { p_attempt_id: attemptId });

    if (error) {
      logger.error('Error cancelling attempt:', error);
    }
  },

  /**
   * Finish. The database marks the answers it stored through checkAnswer and
   * sets the score itself (submit_attempt); the browser no longer sends one.
   * Past the time limit it records a time-out instead. Calling it twice
   * returns the same result.
   */
  async submitQuizAttempt(attemptId: string): Promise<{ score: number; passingScore: number; timedOut: boolean }> {
    logger.log('Submitting quiz attempt:', { attemptId });

    try {
      const { data, error: attemptError } = await supabase.rpc('submit_attempt', { p_attempt_id: attemptId });

      if (attemptError) {
        logger.error('Error in submitQuizAttempt:', attemptError);
        throw attemptError;
      }

      logger.log('Quiz attempt marked:', data);

      // Deliberately does NOT create a certificate here. Issuance is fully
      // server-side: the issue_certificate_on_pass trigger creates the row in
      // the same transaction as the attempt update, and the
      // generate-certificate function renders the PDF and emails it. The old
      // client-side path (direct insert + create_certificate RPC fallback +
      // a Power Automate webhook) is gone, along with the RPC and the
      // SharePoint flow it fed — recording the attempt is this method's
      // whole job.

      // Deliberately does NOT notify admins that someone completed a module.
      // This used to fan out to every admin on every pass — ~2,000 emails per
      // module across 689 staff — which is why quiz-completion emails were
      // switched off on 16 July. Admins have the analytics page; nobody needs
      // 689 people's results in their inbox.
      //
      // It was broken anyway: it read attempt.user.full_name, but the select
      // above never joins `user`, so this threw a TypeError on every single
      // submission and was silently swallowed by the catch.

      return {
        score: Number(data.score),
        passingScore: Number(data.passing_score),
        timedOut: !!data.timed_out,
      };
    } catch (error) {
      logger.error('Fatal error in submitQuizAttempt:', error);
      throw error;
    }
  },

  async getUserAttempts(userId: string) {
    const { data, error } = await supabase
      .from('quiz_attempts')
      .select(`
        *,
        quizzes (
          title,
          passing_score
        )
      `)
      .eq('user_id', userId)
      .order('started_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  async loadQuizzes() {
    const { data: quizzes, error: quizError } = await supabase
      .from('quizzes')
      .select(`
        *,
        questions:quiz_questions(*)
      `)
      .order('order');

    if (quizError) throw quizError;

    // Transform the data to match our frontend types
    return quizzes.map((quiz: any) => {
      // Get questions and sort them by order
      const questions = quiz.questions || [];
      // Ensure questions have an order property and sort them
      const sortedQuestions = [...questions]
        .map((q, index) => ({
          ...q,
          order: q.order ?? index // Use existing order or fallback to index
        }))
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      logger.log('Sorted questions for quiz:', quiz.title, sortedQuestions.map(q => ({
        id: q.id,
        text: q.text,
        order: q.order
      })));

      return {
        id: quiz.id,
        title: quiz.title,
        description: quiz.description,
        passingScore: quiz.passing_score,
        mediaUrl: quiz.media_url,
        isEnabled: quiz.is_enabled,
        order: quiz.order,
        hasIntroduction: quiz.has_introduction,
        introductionTitle: quiz.introduction_title,
        introductionContent: quiz.introduction_content,
        introductionMediaUrl: quiz.introduction_media_url,
        questions: sortedQuestions.map((q: any) => ({
          id: q.id,
          type: q.type,
          text: q.text,
          thumbnailUrl: q.thumbnail_url,
          options: q.options || [],
          correctOptions: Array.isArray(q.correct_options) ? q.correct_options.map(String) : [],
          explanation: q.explanation,
          numberOfGuesses: q.number_of_guesses,
          hotspots: q.hotspots || [],
          differenceUrl: q.difference_url,
          differenceHotspots: q.difference_hotspots,
          videoUrl: q.video_url,
          videoType: q.video_type,
          videoStartTime: q.video_start_time,
          videoEndTime: q.video_end_time,
          order: q.order,
          // Audio support fields
          questionAudioUrl: q.question_audio_url,
          explanationAudioUrl: q.explanation_audio_url,
          questionAudioDuration: q.question_audio_duration,
          explanationAudioDuration: q.explanation_audio_duration,
          useAudio: q.use_audio || false,
          keyTakeawaysHeading: q.key_takeaways_heading,
          answerQuestion: q.answer_question,
          answerText: q.answer_text,
          weight: q.weight || 0,
        }))
      };
    });
  },

  async updateQuiz(quiz: Quiz) {
    logger.log('Starting quiz update for quiz:', quiz.id);
    let backup;
    
    try {
      // Create backup before updating
      backup = await backupService.createBackup(quiz.id);
      logger.log('Backup created successfully:', backup.id);

      // Proceed with update
      const { error: quizError } = await supabase
        .from('quizzes')
        .update({
          title: quiz.title,
          description: quiz.description,
          passing_score: quiz.passingScore,
          media_url: quiz.mediaUrl,
          is_enabled: quiz.isEnabled,
          is_hidden: quiz.isHidden,
          order: quiz.order,
          has_introduction: quiz.hasIntroduction,
          introduction_title: quiz.introductionTitle,
          introduction_content: quiz.introductionContent,
          introduction_media_url: quiz.introductionMediaUrl,
          assigned_users: quiz.assignedUsers || [],
          renewal_months: quiz.renewalMonths ?? null
        })
        .eq('id', quiz.id);

      if (quizError) {
        throw new Error(`Failed to update module: ${quizError.message}`);
      }

      // Handle question updates: delete removed questions, then upsert remaining ones
      logger.log('Updating questions for quiz:', quiz.id);
      
      // First, get current questions from database
      const { data: currentQuestions, error: getCurrentError } = await supabase
        .from('questions')
        .select('id')
        .eq('quiz_id', quiz.id);

      if (getCurrentError) {
        throw new Error(`Failed to get current questions: ${getCurrentError.message}`);
      }

      // Find questions that need to be deleted (exist in DB but not in new questions array)
      const newQuestionIds = quiz.questions.map(q => q.id).filter(id => id); // Only existing questions have IDs
      const questionsToDelete = (currentQuestions || [])
        .filter(q => !newQuestionIds.includes(q.id))
        .map(q => q.id);

      logger.log('Questions to delete:', questionsToDelete);
      logger.log('Questions to upsert:', newQuestionIds);

      // Delete removed questions
      if (questionsToDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from('questions')
          .delete()
          .in('id', questionsToDelete);

        if (deleteError) {
          throw new Error(`Failed to delete questions: ${deleteError.message}`);
        }
        logger.log('Successfully deleted questions:', questionsToDelete.length);
      }

      // Upsert remaining questions
      const mappedQuestions = quiz.questions.map((q: Question) => ({
          id: q.id, // Preserve the question ID for existing questions
          quiz_id: quiz.id,
          type: q.type,
          text: q.text,
          thumbnail_url: q.thumbnailUrl || '',
          options: q.options || [],
          correct_options: q.correctOptions || [],
          explanation: q.explanation || '',
          number_of_guesses: q.numberOfGuesses || 1,
          hotspots: q.hotspots || [],
          video_url: q.videoUrl || '',
          video_type: q.videoType || 'file',
          video_start_time: q.videoStartTime || 0,
          video_end_time: q.videoEndTime || 0,
          minimum_read_time: q.minimumReadTime,
          image_orientation: q.imageOrientation,
          policy_url: q.policyUrl || '',
          order: q.order || 0,
          // Audio support fields
          question_audio_url: q.questionAudioUrl || '',
          explanation_audio_url: q.explanationAudioUrl || '',
          question_audio_duration: q.questionAudioDuration,
          explanation_audio_duration: q.explanationAudioDuration,
          use_audio: q.useAudio || false,
          key_takeaways_heading: q.keyTakeawaysHeading || null,
          answer_question: q.answerQuestion || null,
          answer_text: q.answerText || null,
          weight: q.weight || 0,
        }));

      logger.log('Mapped questions for database (showing first question):', JSON.stringify(mappedQuestions[0], null, 2));
      logger.log('Key takeaways heading values:', mappedQuestions.map(q => ({ id: q.id, type: q.type, heading: q.key_takeaways_heading })));
      logger.log('Click-for-answer fields:', mappedQuestions.filter(q => q.type === 'click-for-answer').map(q => ({
        id: q.quiz_id,
        answer_question: q.answer_question,
        answer_text: q.answer_text
      })));

      const { data: updatedQuestions, error: updateError } = await supabase
        .from('questions')
        .upsert(mappedQuestions, {
          onConflict: 'id'
        })
        .select();

      if (updateError) {
        throw new Error(`Failed to update questions: ${updateError.message}`);
      }

      logger.log('Successfully updated questions:', updatedQuestions?.length || 0);

      // Trigger server-side audio generation for all questions in the module
      logger.log('Triggering server-side audio generation for quiz:', quiz.id);
      try {
        const { unsubscribe } = await generateAudioServerSide(quiz.id, (progress) => {
          logger.log(`Audio generation progress: ${progress.completedItems}/${progress.totalItems} (${progress.status})`);
          if (progress.status === 'completed' || progress.status === 'failed' || progress.status === 'cancelled') {
            unsubscribe();
          }
        });
      } catch (audioError) {
        logger.error('Audio generation failed:', audioError);
        // Don't throw - quiz update was successful, audio generation is a bonus feature
        // User can manually regenerate audio later if needed
      }

    } catch (error) {
      logger.error('Error during quiz update:', error);
      
      // If anything fails and we have a backup, restore from it
      if (backup) {
        logger.log('Attempting to restore from backup:', backup.id);
        try {
          await backupService.restoreFromBackup(backup.id);
          logger.log('Successfully restored from backup');
        } catch (restoreError) {
          logger.error('Failed to restore from backup:', restoreError);
          throw new Error(`Update failed and restore failed: ${error}. Restore error: ${restoreError}`);
        }
      }
      
      throw error;
    }
  },

  backupService, // Export backup service for direct access
}; 